"use client";

import { Switch as SwitchPrimitive } from "@base-ui/react/switch";

import { cn } from "@/lib/utils";

/**
 * O interruptor da mesa.
 *
 * O tema aqui NÃO é o do preset, e é de propósito: a casa desenha 42×24 com um
 * círculo de 18 (o preset traz 44×20 com um botão largo, de pílula), e ligado é
 * VERDE — a cor do que é da IA e do que está ativo no escritório —, não a
 * terracota da ação. O botão é papel (`bg-card`) e não o bege da mesa: sobre o
 * verde, o bege lê como apagado.
 *
 * Isto mora no componente porque medida de controle é decisão do sistema de
 * design, não de quem usa: tentar corrigir de fora exige variante de filho
 * (`[&>[data-slot=switch-thumb]]:size-[18px]`) disputando especificidade com as
 * classes que o próprio thumb traz — e o resultado é o interruptor torto.
 *
 * O que vem de acessibilidade, e não é enfeite:
 * - `role=switch` + `aria-checked` são da primitiva; o nome vem de quem usa
 *   (`aria-label` no `Alternador`) — `data-dica` não é nome acessível.
 * - **ponteiro de mão** e resposta ao passar o mouse: o trilho escurece
 *   desligado, clareia ligado, e o botão cresce 5%. Era a única peça da mesa que
 *   não dizia nada ao ponteiro — quem não vê o estado mudar acha que não clicou.
 * - **alvo maior que o desenho**: o `after:-inset-x-3 after:-inset-y-2` estende a
 *   área de toque uns 12px além do trilho de 42×24, que é menor que o mínimo de
 *   dedo. O desenho continua pequeno; o alvo, não.
 * - anel de foco do teclado (`focus-visible:ring-3 ring-ring/30`, a terracota da
 *   casa) e `cursor-not-allowed` + opacidade quando desabilitado.
 * - **tudo em PIXEL** (02/11): a altura e o deslize estavam em `rem` (`h-6`,
 *   `translate-x-5`) e a largura e o botão em px — com a fonte do navegador
 *   maior que o padrão o trilho crescia, o botão andava 25px num trilho de 38
 *   por dentro e saía pela ponta.
 * - `transition` no botão, não no trilho inteiro: quem pede menos movimento
 *   (`prefers-reduced-motion`, em `acesso.css`) desliga tudo num lugar só.
 */
function Switch({
  className,
  size = "default",
  ...props
}: SwitchPrimitive.Root.Props & {
  size?: "sm" | "default";
}) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      data-size={size}
      className={cn(
        "peer group/switch relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 transition-all outline-none group-has-[:focus-visible]/field-label:ring-0 after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 data-[size=default]:h-[24px] data-[size=default]:w-[42px] data-[size=sm]:h-[20px] data-[size=sm]:w-[36px] dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 data-checked:border-verde data-checked:bg-verde data-checked:hover:border-verde/80 data-checked:hover:bg-verde/80 group-has-[:focus-visible]/field-label:data-checked:border-verde data-unchecked:border-transparent data-unchecked:bg-[rgba(21,23,26,.18)] data-unchecked:hover:bg-[rgba(21,23,26,.28)] group-has-[:focus-visible]/field-label:data-unchecked:border-transparent data-disabled:cursor-not-allowed data-disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className="pointer-events-none block rounded-full bg-card shadow-[0_1px_3px_rgba(0,0,0,.2)] ring-0 transition-transform not-dark:bg-clip-padding group-data-[size=default]/switch:size-[18px] group-data-[size=sm]/switch:size-[14px] group-hover/switch:scale-105 data-checked:translate-x-[20px] group-data-[size=sm]/switch:data-checked:translate-x-[16px] data-unchecked:translate-x-0 dark:bg-card"
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
